/*
 * Guillo AW26 immersive homepage — scene controller
 *
 * One deliberate gesture (wheel, trackpad, swipe, key) moves exactly one
 * full-screen scene. Scenes are ordinary stacked Shopify sections until this
 * controller activates (progressive enhancement: no JS = normal scrolling).
 *
 * Assistant shell (phase two) frontend interface — dispatched on document:
 *   guillo:assistant-submit   detail: { query: String }   fired when user submits
 *   guillo:assistant-loading  detail: { on: Boolean }     toggle the loading spinner
 *   guillo:assistant-result   detail: { html: String }    insert a result scene
 *   guillo:assistant-error    detail: { message: String }
 *   guillo:assistant-close    detail: {}
 * The shell only renders when the "AW26 immersive" theme setting enables it,
 * and performs no network requests in phase one.
 */
(function () {
  'use strict';

  var WHEEL_THRESHOLD = 26;
  var TRANSITION_DURATION = 660;
  var TOUCH_THRESHOLD = 48;
  var WHEEL_SILENCE_RESET = 160;
  var MOMENTUM_QUIET_GAP = 150;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var designMode = window.Shopify && window.Shopify.designMode;

  function GScenes() {
    this.slots = [];
    this.current = 0;
    this.locked = false;
    this.wheelAcc = 0;
    this.wheelTimer = null;
    this.momentum = false;
    this.lastWheelAt = 0;
    this.overlayCount = 0;
    this.touch = null;
    this.listeners = [];
    this.init();
  }

  GScenes.prototype.on = function (target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this.listeners.push([target, type, fn, opts]);
  };

  GScenes.prototype.init = function () {
    var scenes = document.querySelectorAll('[data-gscene]');
    if (scenes.length < 2) return;
    var self = this;

    // Every top-level homepage section becomes a scene slot, in template
    // order. Sections without a [data-gscene] scene (any ordinary theme or
    // app section added in the editor) become self-scrolling generic slots,
    // so merchants can slot normal content between the immersive scenes.
    var stage = document.querySelector('.main-content');
    var sections = stage ? stage.querySelectorAll(':scope > .shopify-section') : [];
    Array.prototype.forEach.call(sections, function (sec) {
      if (sec.querySelector('[data-gscene]')) {
        sec.classList.add('gscene-slot');
      } else {
        if (!sec.firstElementChild) return; // empty wrapper: skip
        sec.classList.add('gscene-slot', 'gscene-slot--generic');
        sec.setAttribute('data-gscene-vscroll', '');
      }
      self.slots.push(sec);
    });
    // scenes rendered outside .main-content (defensive fallback)
    scenes.forEach(function (scene) {
      var slot = scene.closest('.shopify-section') || scene;
      if (self.slots.indexOf(slot) === -1) {
        slot.classList.add('gscene-slot');
        self.slots.push(slot);
      }
    });

    document.documentElement.classList.add('gscenes-on');
    if (reducedMotion.matches) document.documentElement.classList.add('gscenes-reduced');

    this.applyPositions(true);
    this.syncHeader();
    this.prepareMedia(0);
    this.prepareMedia(1);
    this.playActiveVideos();

    // -- input --------------------------------------------------------------
    this.on(window, 'wheel', this.onWheel.bind(this), { passive: false });
    this.on(window, 'keydown', this.onKey.bind(this));
    this.on(window, 'touchstart', this.onTouchStart.bind(this), { passive: true });
    this.on(window, 'touchmove', this.onTouchMove.bind(this), { passive: false });
    this.on(window, 'touchend', this.onTouchEnd.bind(this), { passive: true });
    this.on(window, 'resize', this.applyPositions.bind(this, true));

    // Pause scene navigation while theme drawers / modals lock scroll.
    this.on(document, 'theme:scroll:lock', function () { self.overlayCount++; });
    this.on(document, 'theme:scroll:unlock', function () {
      self.overlayCount = Math.max(0, self.overlayCount - 1);
    });

    // Keyboard users tabbing into another scene bring it into view.
    this.on(document, 'focusin', function (e) {
      var slot = e.target.closest && e.target.closest('.gscene-slot');
      if (!slot) return;
      var i = self.slots.indexOf(slot);
      if (i !== -1 && i !== self.current) self.goTo(i, true);
    });

    // [data-gscene-next] / [data-gscene-prev] controls move one scene.
    this.on(document, 'click', function (e) {
      if (!e.target.closest) return;
      if (e.target.closest('[data-gscene-next]')) {
        e.preventDefault();
        self.goTo(self.current + 1);
      } else if (e.target.closest('[data-gscene-prev]')) {
        e.preventDefault();
        self.goTo(self.current - 1);
      }
    });

    // -- Theme Editor ---------------------------------------------------------
    if (designMode) {
      this.on(document, 'shopify:section:select', function (e) {
        var slot = e.target.closest ? e.target : null;
        var i = self.slots.indexOf(slot || e.target);
        if (i === -1 && e.target.querySelector) {
          var inner = e.target.classList.contains('gscene-slot') ? e.target : null;
          i = self.slots.indexOf(inner);
        }
        if (i !== -1) self.goTo(i, true);
      });
    }

    // header mode follows generic-slot scrolling (white logo once imagery
    // slides beneath the header)
    var headerSyncQueued = false;
    this.on(document, 'scroll', function (e) {
      var active = self.slots[self.current];
      if (!active || e.target !== active || !active.classList.contains('gscene-slot--generic')) return;
      if (headerSyncQueued) return;
      headerSyncQueued = true;
      requestAnimationFrame(function () {
        headerSyncQueued = false;
        self.syncHeader();
      });
    }, { capture: true, passive: true });

    this.assistantShell();
  };

  // -- journey mode -----------------------------------------------------
  // The assistant mounts generated scenes into its own stage; the same
  // controller (inputs, transitions, video lifecycle) drives them.

  GScenes.prototype.enterJourney = function (stage) {
    if (!this.homeState) {
      this.homeState = { slots: this.slots, index: this.current };
    }
    this.slots = Array.prototype.slice.call(stage.querySelectorAll('.gscene-slot'));
    this.current = 0;
    this.locked = false;
    this.wheelAcc = 0;
    this.momentum = false;
    this.applyPositions(true);
    this.syncHeader();
    this.prepareMedia(0);
    this.prepareMedia(1);
    this.playActiveVideos();
  };

  GScenes.prototype.exitJourney = function () {
    if (!this.homeState) return;
    this.pauseAllVideos();
    this.slots = this.homeState.slots;
    this.current = this.homeState.index;
    this.homeState = null;
    this.locked = false;
    this.wheelAcc = 0;
    this.momentum = false;
    this.applyPositions(true);
    this.syncHeader();
    this.playActiveVideos();
  };

  GScenes.prototype.destroy = function () {
    this.listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
    this.listeners = [];
    document.documentElement.classList.remove('gscenes-on', 'gscenes-reduced');
    this.slots.forEach(function (s) {
      s.classList.remove('gscene-slot', 'gscene-slot--active', 'gscene-slot--generic');
      s.removeAttribute('data-gscene-vscroll');
      s.style.transform = '';
      s.style.transition = '';
      s.style.visibility = '';
    });
  };

  // -- positioning ------------------------------------------------------------

  GScenes.prototype.applyPositions = function (instant) {
    var self = this;
    this.slots.forEach(function (slot, i) {
      var y = i === self.current ? '0' : (i < self.current ? '-100%' : '100%');
      if (instant) slot.style.transition = 'none';
      slot.style.transform = 'translate3d(0,' + y + ',0)';
      // Keep only neighbours in the paint tree.
      var near = Math.abs(i - self.current) <= 1;
      slot.style.visibility = near ? 'visible' : 'hidden';
      slot.style.willChange = near ? 'transform' : 'auto';
      slot.classList.toggle('gscene-slot--active', i === self.current);
      if (instant) {
        void slot.offsetHeight; // flush so the next transform animates
        slot.style.transition = '';
      }
    });
  };

  GScenes.prototype.goTo = function (index, instant) {
    var self = this;
    index = Math.max(0, Math.min(this.slots.length - 1, index));
    if (index === this.current || this.locked) return;

    var useInstant = instant || reducedMotion.matches;
    this.locked = true;
    this.pauseAllVideos();
    this.prepareMedia(index);
    this.prepareMedia(index + (index > this.current ? 1 : -1));

    this.current = index;
    this.applyPositions(useInstant);
    this.syncHeader();

    // unlock when the incoming scene actually lands (transitionend), with a
    // fallback timer; residual trackpad momentum is handled separately by
    // the quiet-gap guard, so no long hard-coded lock is needed.
    var target = this.slots[index];
    var settled = false;
    var settle = function () {
      if (settled) return;
      settled = true;
      target.removeEventListener('transitionend', onEnd);
      self.locked = false;
      self.wheelAcc = 0;
      self.playActiveVideos();
      self.releaseFarMedia();
      self.slots.forEach(function (slot, i) {
        slot.style.visibility = Math.abs(i - self.current) <= 1 ? 'visible' : 'hidden';
      });
      document.dispatchEvent(new CustomEvent('gscene:change', {
        detail: { index: self.current, scene: self.slots[self.current] }
      }));
    };
    var onEnd = function (e) {
      if (e.target === target && e.propertyName === 'transform') settle();
    };
    if (useInstant) {
      window.setTimeout(settle, 50);
    } else {
      target.addEventListener('transitionend', onEnd);
      window.setTimeout(settle, TRANSITION_DURATION + 140); // fallback
    }
  };

  GScenes.prototype.syncHeader = function () {
    var slot = this.slots[this.current];
    if (!slot) return;
    var scene = slot.querySelector('[data-gscene]');
    // Generic slots start on a white canvas (dark controls), but once their
    // content scrolls up beneath the header, imagery slides behind it — flip
    // to light controls with the header's dark veil so the logo stays visible.
    var fallback = 'light';
    if (slot.classList.contains('gscene-slot--generic')) {
      fallback = slot.scrollTop > 8 ? 'light' : 'dark';
    }
    var mode = (scene && scene.getAttribute('data-gheader')) || fallback;
    document.documentElement.setAttribute('data-gheader', mode);
  };

  // -- media ----------------------------------------------------------------

  GScenes.prototype.prepareMedia = function (index) {
    if (index < 0 || index >= this.slots.length) return;
    this.slots[index].querySelectorAll('video[data-gscene-video]').forEach(function (v) {
      if (v.dataset.gsceneLoaded) return;
      v.querySelectorAll('source[data-src]').forEach(function (s) {
        s.src = s.dataset.src;
        s.removeAttribute('data-src');
      });
      v.dataset.gsceneLoaded = '1';
      try { v.load(); } catch (e) { /* noop */ }
    });
    this.slots[index].querySelectorAll('img[loading="lazy"]').forEach(function (img) {
      img.loading = 'eager';
    });
  };

  GScenes.prototype.releaseFarMedia = function () {
    var self = this;
    this.slots.forEach(function (slot, i) {
      if (Math.abs(i - self.current) <= 1) return;
      slot.querySelectorAll('video[data-gscene-video]').forEach(function (v) {
        if (!v.dataset.gsceneLoaded) return;
        if (!v.paused) v.pause();
        v.querySelectorAll('source[src]').forEach(function (s) {
          s.dataset.src = s.src;
          s.removeAttribute('src');
        });
        delete v.dataset.gsceneLoaded;
        try { v.load(); } catch (e) { /* frees the decoder; poster remains */ }
      });
    });
  };

  GScenes.prototype.playActiveVideos = function () {
    if (reducedMotion.matches) return; // posters only
    this.slots[this.current].querySelectorAll('video[data-gscene-video]').forEach(function (v) {
      var p = v.play();
      if (p && p.catch) p.catch(function () { /* autoplay blocked: poster remains */ });
    });
    this.syncBackgroundVideos();
  };

  GScenes.prototype.pauseAllVideos = function () {
    document.querySelectorAll('video[data-gscene-video]').forEach(function (v) {
      if (!v.paused) v.pause();
    });
  };

  // Blurred-portrait scenes render the same footage twice (sharp + blurred).
  // Keep the pair closely synchronised without a constant correction loop.
  GScenes.prototype.syncBackgroundVideos = function () {
    var scene = this.slots[this.current];
    scene.querySelectorAll('[data-gscene-portrait]').forEach(function (wrap) {
      var main = wrap.querySelector('video[data-gscene-main]');
      var bg = wrap.querySelector('video[data-gscene-bg]');
      if (!main || !bg || wrap.dataset.gsceneSynced) return;
      wrap.dataset.gsceneSynced = '1';
      window.setInterval(function () {
        if (main.paused || bg.paused) return;
        if (Math.abs(main.currentTime - bg.currentTime) > 0.35) {
          bg.currentTime = main.currentTime;
        }
      }, 2500);
    });
  };

  // -- gesture handling -------------------------------------------------------

  GScenes.prototype.blocked = function (target, e) {
    if (this.overlayCount > 0) return true;
    if (document.body.hasAttribute('data-drawer-open')) return true;
    if (!target || !target.closest) return false;
    if (target.closest(
      'input, textarea, select, [contenteditable], .drawer, .quickview__modal, ' +
      '.search--popdown, [data-modal-container], .gassistant'
    )) return true;
    // Horizontal rails only claim the gesture when they can actually scroll
    // and the gesture is primarily horizontal (or a horizontal touch).
    var rail = target.closest('[data-gscene-hscroll]');
    if (rail && rail.scrollWidth > rail.clientWidth + 4) {
      if (!e) return true; // touch intent is resolved in onTouchMove
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return true;
    }
    // Vertically scrollable panes (e.g. the shop grid) keep the wheel until
    // they hit an edge; then the gesture advances the scene as usual.
    // Touch intent is resolved in onTouchMove via this.touch.pane.
    if (e) {
      var pane = target.closest('[data-gscene-vscroll]');
      if (pane && pane.scrollHeight > pane.clientHeight + 4) {
        var down = e.deltaY > 0;
        // tolerate fractional scrollTop (retina/zoom) or the edge never registers
        var atTop = pane.scrollTop <= 2;
        var atBottom = pane.scrollTop + pane.clientHeight >= pane.scrollHeight - 2;
        if ((down && !atBottom) || (!down && !atTop)) return true;
      }
    }
    return false;
  };

  GScenes.prototype.onWheel = function (e) {
    if (designMode) return;
    if (this.blocked(e.target, e)) return;
    e.preventDefault();

    var now = performance.now();
    var gap = now - this.lastWheelAt;
    this.lastWheelAt = now;

    if (this.locked) {
      this.momentum = true; // events during a transition belong to the old gesture
      return;
    }
    if (this.momentum) {
      if (gap < MOMENTUM_QUIET_GAP) return; // residual momentum: ignore
      this.momentum = false; // quiet gap passed: a new gesture may begin
    }

    var delta = e.deltaY;
    if (e.deltaMode === 1) delta *= 16;
    if (e.deltaMode === 2) delta *= window.innerHeight;
    this.wheelAcc += delta;

    var self = this;
    window.clearTimeout(this.wheelTimer);
    this.wheelTimer = window.setTimeout(function () { self.wheelAcc = 0; }, WHEEL_SILENCE_RESET);

    if (this.wheelAcc > WHEEL_THRESHOLD) {
      this.wheelAcc = 0;
      this.momentum = true;
      this.goTo(this.current + 1);
    } else if (this.wheelAcc < -WHEEL_THRESHOLD) {
      this.wheelAcc = 0;
      this.momentum = true;
      this.goTo(this.current - 1);
    }
  };

  GScenes.prototype.onKey = function (e) {
    if (designMode) return;
    if (this.blocked(e.target)) return;
    var map = {
      ArrowDown: this.current + 1,
      PageDown: this.current + 1,
      ArrowUp: this.current - 1,
      PageUp: this.current - 1,
      Home: 0,
      End: this.slots.length - 1
    };
    if (map[e.key] === undefined) return;
    e.preventDefault();
    this.goTo(map[e.key]);
  };

  GScenes.prototype.onTouchStart = function (e) {
    if (this.blocked(e.target)) { this.touch = null; return; }
    var pane = e.target.closest && e.target.closest('[data-gscene-vscroll]');
    this.touch = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      fired: false,
      horizontal: false,
      pane: (pane && pane.scrollHeight > pane.clientHeight + 4) ? pane : null
    };
  };

  GScenes.prototype.onTouchMove = function (e) {
    if (!this.touch || this.touch.fired || this.locked) return;
    var dx = e.touches[0].clientX - this.touch.x;
    var dy = e.touches[0].clientY - this.touch.y;

    if (!this.touch.horizontal && Math.abs(dx) > 14 && Math.abs(dx) > Math.abs(dy)) {
      this.touch.horizontal = true; // hand the gesture to carousels
    }
    if (this.touch.horizontal) return;

    // inside a scrollable pane: let it scroll natively until it hits an edge
    if (this.touch.pane) {
      var p = this.touch.pane;
      var scrollingDown = dy < 0;
      var atTop = p.scrollTop <= 2;
      var atBottom = p.scrollTop + p.clientHeight >= p.scrollHeight - 2;
      if ((scrollingDown && !atBottom) || (!scrollingDown && !atTop)) return;
    }

    if (Math.abs(dy) > Math.abs(dx)) e.preventDefault();

    if (Math.abs(dy) > TOUCH_THRESHOLD) {
      this.touch.fired = true;
      this.goTo(this.current + (dy < 0 ? 1 : -1));
    }
  };

  GScenes.prototype.onTouchEnd = function () {
    this.touch = null;
  };

  // -- assistant shell (phase one: visual only, disabled by default) ---------

  GScenes.prototype.assistantShell = function () {
    var shell = document.querySelector('[data-gassistant]');
    if (!shell) return;
    var form = shell.querySelector('form');
    var input = shell.querySelector('input');
    this.on(form, 'submit', function (e) {
      e.preventDefault();
      var query = (input.value || '').trim();
      if (!query) return;
      document.dispatchEvent(new CustomEvent('guillo:assistant-submit', { detail: { query: query } }));
    });
    this.on(document, 'guillo:assistant-loading', function (e) {
      shell.classList.toggle('gassistant--loading', !!(e.detail && e.detail.on));
    });
  };

  // -- boot -------------------------------------------------------------------

  function boot() {
    if (window.__gscenes) window.__gscenes.destroy();
    window.__gscenes = new GScenes();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', boot);
    document.addEventListener('shopify:section:unload', boot);
    document.addEventListener('shopify:section:reorder', boot);
  }
})();
